// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { AlertCircle, Loader2, Building2 } from 'lucide-react'
import PasswordField from '../components/PasswordField'
import { useAuth } from '../auth/AuthContext'
import { resolvePharmacyFromUrl, looksLikePharmacyCode, safeNextPath } from '../auth/pharmacyUrl'
import MfaCodePrompt from '../components/MfaCodePrompt'
import MfaEnrollPrompt from '../components/MfaEnrollPrompt'

interface Props { onScreenEnter: () => void }

interface Branding {
  id: string
  name: string
  display_name: string
  slug: string
  /** Opaque, high-entropy pharmacy code. Preferred over slug for building the post-login URL. */
  url_code: string | null
  logo_url: string | null
  primary_color_hex: string | null
  sso_provider: string
}

function loginErrorMessage(code: string): string {
  switch (code) {
    case 'invalid_credentials':
      return 'That email and password don’t match an active account.'
    case 'account_locked':
      return 'This account is locked due to too many failed login attempts. Try again later or contact your administrator.'
    case 'account_disabled':
      return 'This account has been disabled. Contact your administrator.'
    case 'account_pending':
      return 'This account hasn’t finished setup yet. Check your email for the invitation link.'
    case 'email_password_required':
      return 'Please enter both your email and password.'
    case 'rate_limited':
      return 'Too many attempts. Please wait a moment and try again.'
    case 'sso_required':
      return 'Your pharmacy requires single sign-on. Please use the “Sign in with SSO” button below instead of a password.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}

/**
 * Per-pharmacy login page.
 *
 * Route: /p/:slug/login
 *
 * Same form as /login but branded with the pharmacy's name + logo + primary
 * color. After successful sign-in, redirects to home (`/`) where the user's
 * pharmacy_id (from /auth/me) drives the rest of the UI.
 *
 * Branding is fetched from the public endpoint
 * GET /api/public/pharmacy-branding/{slug} (no auth needed).
 *
 * If the slug is unknown or archived → 404 message.
 *
 * Currently this is a path-based placeholder (per WI-895 plan). Once DNS
 * is configured, the same page will also serve the subdomain
 * `{slug}.owlrxmu.owlconsultations.com/login` — no React change needed,
 * just routing infra. The component reads the slug from either source.
 */
export default function PharmacyLoginPage({ onScreenEnter }: Props) {
  // Under the /p/{slug} basename there is no :slug route param, so fall back to the URL.
  const { slug: paramSlug } = useParams<{ slug: string }>()
  const slug = paramSlug ?? resolvePharmacyFromUrl().slug ?? ''
  const { me, role, loading, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [branding, setBranding] = useState<Branding | null>(null)
  const [brandingLoading, setBrandingLoading] = useState(true)
  const [brandingError, setBrandingError] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [mfaEnroll, setMfaEnroll] = useState(false)

  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  // Fetch branding. The /p/{x} identifier may be an opaque url_code (preferred) or a legacy slug.
  // Try the by-code endpoint first when it looks like a code, then fall back to the slug endpoint so
  // existing slug links keep working until the slug endpoint is retired (migration S6).
  useEffect(() => {
    if (!slug) {
      setBrandingError('No pharmacy specified')
      setBrandingLoading(false)
      return
    }
    let cancelled = false
    async function load() {
      try {
        if (looksLikePharmacyCode(slug)) {
          const rc = await fetch(`/api/public/pharmacy-branding-by-code/${encodeURIComponent(slug)}`)
          if (cancelled) return
          if (rc.ok) { setBranding(await rc.json() as Branding); return }
          if (rc.status !== 404) { setBrandingError('Could not load pharmacy information. Please try again.'); return }
          // 404 on the code endpoint → fall through to slug (the value may be a hex-shaped slug).
        }
        const r = await fetch(`/api/public/pharmacy-branding/${encodeURIComponent(slug)}`)
        if (cancelled) return
        if (r.ok) {
          const b = await r.json() as Branding
          // Name route is RETIRED: only the opaque /p/{url_code} plug is allowed. If we resolved via
          // the legacy name-slug, redirect to the canonical code URL before rendering any login form,
          // so the pharmacy name never appears in (or works as) a login URL. Full-page redirect
          // because the router basename (/p/{slug}) is fixed at load.
          if (b.url_code && slug !== b.url_code && !looksLikePharmacyCode(slug)) {
            window.location.replace(`/p/${b.url_code}/login${window.location.search}${window.location.hash}`)
            return
          }
          setBranding(b)
        } else if (r.status === 404) {
          setBrandingError(`We couldn't find a pharmacy with the URL "${slug}". Check the link from your administrator.`)
        } else {
          setBrandingError('Could not load pharmacy information. Please try again.')
        }
      } catch {
        if (!cancelled) setBrandingError('Network error loading pharmacy information.')
      } finally {
        if (!cancelled) setBrandingLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [slug])

  // Bounce into the pharmacy workspace after sign-in — to the return-to target (e.g. the document
  // deep-link the user arrived on) when present, else the pharmacy home. Basename keeps us under /p/{slug}.
  useEffect(() => {
    if (loading) return
    if (me?.authenticated) {
      // D-MFR-1 — an external manufacturer goes straight to its standalone portal, never
      // through the pharmacy shell (and never to a pharmacy deep-link it can't access).
      if (role === 'manufacturer') { navigate('/manufacturer', { replace: true }); return }
      // A FACILITY account has no pharmacy shell either. Without this it authenticated, got
      // bounced to /p/{slug}, was rejected there by RequireRole (the pharmacy routes do not admit
      // 'facility'), redirected to '/', which under the /p/{slug} basename is the pharmacy home,
      // which sent it back to this page — which saw me.authenticated and bounced it again.
      // Measured in production: ~940 requests and climbing, the page stuck on "Loading pharmacy…".
      // Its portal is /facility — a standalone route outside the shell, exactly like
      // /manufacturer. This said /provider until 2026-09-25, which stopped the loop but landed
      // facility users on the PRESCRIBER dashboard; FacilityDashboard was routed nowhere.
      if (role === 'facility') { navigate('/facility', { replace: true }); return }
      const from = (location.state as any)?.from as { pathname?: string; search?: string } | undefined
      const fromPath = from?.pathname ? from.pathname + (from.search ?? '') : undefined
      // NEXT-1: state.from when it survived, else the ?next carried through the hard
      // redirects (validated — relative same-app paths only, never /login, never off-app).
      const next = safeNextPath(new URLSearchParams(location.search).get('next'))
      // D-EMAIL-1 guard: on a pharmacy-scoped login, an already-authenticated session must bounce to
      // the pharmacy HOME (/p/{slug}), never bare '/' — on the apex host '/' is the marketing landing,
      // so a residual session (e.g. the admin who issued an invite in this browser) would surface
      // marketing instead of the login form. Falls back to '/' only when there's no pharmacy slug.
      navigate(fromPath || next || (slug ? `/p/${slug}` : '/'), { replace: true })
    }
  }, [loading, me, role, navigate, slug, location.state])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (!email.trim() || !password) {
      setError(loginErrorMessage('email_password_required'))
      return
    }
    setSubmitting(true)
    const result = await signIn(email.trim(), password, branding?.slug)
    setSubmitting(false)
    if (result.mfaEnrollmentRequired && result.mfaToken) {
      setMfaToken(result.mfaToken); setMfaEnroll(true)
      return
    }
    if (result.mfaRequired && result.mfaToken) {
      setMfaToken(result.mfaToken)
      return
    }
    if (!result.ok) {
      setError(loginErrorMessage(result.error ?? 'login_failed'))
    }
  }

  if (brandingLoading) {
    return (
      <div style={{ maxWidth: 440, margin: '0 auto', padding: '32px 24px', textAlign: 'center' }}>
        <Loader2 size={20} className="spin" aria-hidden="true" />
        <p style={{ color: 'var(--color-text-soft)', marginTop: 12 }}>Loading…</p>
      </div>
    )
  }

  if (brandingError || !branding) {
    return (
      <div style={{ maxWidth: 440, margin: '0 auto', padding: '32px 24px' }}>
        <div role="alert" style={errorBlockStyle}>
          <AlertCircle size={18} aria-hidden="true" />
          <div>
            <strong>Pharmacy not found</strong>
            <p style={{ margin: '4px 0 0', fontSize: 13 }}>{brandingError}</p>
          </div>
        </div>
        <p style={{ marginTop: 16, fontSize: 13 }}>
          <Link to="/login" style={linkStyle}>Use the standard sign-in page →</Link>
        </p>
      </div>
    )
  }

  // Apply branding via inline CSS variable on the wrapper
  const accent = branding.primary_color_hex || 'var(--color-primary, #0d9488)'

  return (
    <main style={{
      ['--pharm-accent' as any]: accent,
      maxWidth: 440, margin: '0 auto', padding: '32px 24px',
    }}>

      {/* Pharmacy banner */}
      <div style={pharmacyBannerStyle}>
        {branding.logo_url ? (
          <img src={branding.logo_url} alt="" style={logoStyle} />
        ) : (
          <div style={logoFallbackStyle}>
            <Building2 size={24} aria-hidden="true" />
          </div>
        )}
        <div>
          <div style={pharmacyNameStyle}>{branding.display_name}</div>
          <div style={portalLabelStyle}>Pharmacy Portal · sign in</div>
        </div>
      </div>

      {mfaToken ? (
        mfaEnroll
          ? <MfaEnrollPrompt mfaToken={mfaToken} email={email} onSuccess={() => { /* /me refresh triggers redirect */ }} />
          : <MfaCodePrompt mfaToken={mfaToken} email={email} onSuccess={() => { /* /me refresh triggers redirect */ }} />
      ) : (
      <>
      <form onSubmit={handleSubmit} style={formStyle} noValidate>
        <label style={labelStyle} htmlFor="login-email">Email</label>
        <input
          id="login-email" type="email" autoComplete="username"
          value={email} onChange={e => setEmail(e.target.value)}
          disabled={submitting} required style={inputStyle}
        />

        <label style={labelStyle} htmlFor="login-password">Password</label>
        <PasswordField
          id="login-password" autoComplete="current-password"
          value={password} onChange={setPassword}
          disabled={submitting} required style={inputStyle}
        />

        {error && (
          <div role="alert" style={errorStyle}>
            <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" disabled={submitting} style={submitStyle}>
          {submitting ? (
            <><Loader2 size={16} className="spin" aria-hidden="true" /> Signing in…</>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      {branding.sso_provider && branding.sso_provider !== 'none' && (
        <>
          <div style={ssoDividerStyle}><span style={ssoDividerTextStyle}>or</span></div>
          <button
            type="button"
            style={ssoButtonStyle}
            onClick={() => {
              try { sessionStorage.setItem('owlrx_sso_pending', '1') } catch {}
              // Build the post-login URL from the OPAQUE url_code, not the human-readable slug, so the
              // authenticated URL is /p/{code} (matching the by-code branding + cross-tenant gate) and
              // the legacy name never reappears in the address bar. Fall back to slug only if a code
              // isn't present (older pharmacy rows pre-url_code).
              const postLoginCode = branding.url_code || slug
              window.location.href = `/api/auth/sso/${branding.id}/start?redirect_to=${encodeURIComponent(window.location.origin + '/p/' + postLoginCode)}`
            }}
          >
            <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
              <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Employee login
          </button>
        </>
      )}

      </>
      )}

      <div style={altLinksStyle}>
        <Link to="/forgot-password" style={linkStyle}>Forgot password?</Link>
        <span aria-hidden="true" style={{ color: 'var(--color-text-mute)' }}>•</span>
        <Link to="/accept-invitation" style={linkStyle}>I have an invitation</Link>
      </div>

      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--color-border)', textAlign: 'center', fontSize: 12, lineHeight: 1.6, color: 'var(--color-text-muted)' }}>
        <div style={{ marginBottom: 4 }}>
          This system processes protected health information. Authorized use only; activity may be monitored and audited.
        </div>
        <div style={{ marginBottom: 4 }}>
          © {new Date().getFullYear()} Owl Consultations — proprietary licensed software. Unauthorized
          access or use is prohibited and may be referred for civil and criminal prosecution.
        </div>
        <Link to="/legal/privacy" style={linkStyle}>Privacy Policy</Link>
        {' · '}
        <Link to="/legal/terms" style={linkStyle}>Terms of Use</Link>
        {' · '}
        <Link to="/legal/data-rights" style={linkStyle}>Your Data Rights</Link>
      </div>
    </main>
  )
}

// ---------- styles ----------
const pharmacyBannerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '20px 24px', marginBottom: 24,
  borderRadius: 8,
  background: 'var(--pharm-accent, var(--color-primary))',
  color: '#fff',
}
const logoStyle: React.CSSProperties = {
  width: 56, height: 56, objectFit: 'contain',
  background: '#fff', borderRadius: 8, padding: 6,
}
const logoFallbackStyle: React.CSSProperties = {
  width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(255,255,255,0.18)', borderRadius: 8,
}
const pharmacyNameStyle: React.CSSProperties = {
  fontSize: 18, fontWeight: 600, lineHeight: 1.2,
}
const portalLabelStyle: React.CSSProperties = {
  fontSize: 12, opacity: 0.85, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5,
}
const formStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  padding: 24, background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)', borderRadius: 8,
}
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 500,
  marginTop: 12, marginBottom: 4, color: 'var(--color-text-soft)',
}
const inputStyle: React.CSSProperties = {
  fontSize: 14, padding: '8px 10px',
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
  fontFamily: 'inherit',
}
const errorStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  marginTop: 14, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b',
  borderRadius: 4, fontSize: 13,
}
const errorBlockStyle: React.CSSProperties = {
  display: 'flex', gap: 12, padding: '14px 16px',
  background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b',
  borderRadius: 6,
}
const submitStyle: React.CSSProperties = {
  marginTop: 18, padding: '10px 14px',
  fontSize: 14, fontWeight: 500,
  background: 'var(--pharm-accent, var(--color-primary))',
  color: '#fff', border: 'none', borderRadius: 4,
  cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center',
}
const altLinksStyle: React.CSSProperties = {
  marginTop: 16, fontSize: 13, display: 'flex', gap: 10,
  justifyContent: 'center', alignItems: 'center',
}
const linkStyle: React.CSSProperties = {
  color: 'var(--pharm-accent, var(--color-primary))',
  textDecoration: 'none',
}

const ssoDividerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', textAlign: 'center', margin: '18px 0', color: 'var(--color-text-mute)', fontSize: 12 }
const ssoDividerTextStyle: React.CSSProperties = { padding: '0 12px', background: 'var(--color-surface)', position: 'relative', zIndex: 1 }
const ssoButtonStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '11px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }
